import { Inject } from '@angular/core';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, timeout } from 'rxjs';
import { RegionSelectionService } from './region-selection.service';

export type DashboardSummaryDataSource = 'API' | 'FALLBACK';

export interface DashboardSummarySource {
    table_id: string;
    url: string | null;
}

export interface DashboardSummary {
    reference_year: number | null;
    classification_name: string;
    industry_scope_label: string;
    total_registered_regions: number;
    ready_regions: number;
    pending_regions: number;
    unavailable_regions: number;
    analyzed_industry_count: number;
    national_establishments: number | null;
    national_employees: number | null;
    lq_verified_region_industry_pairs: number;
    updated_at: string | null;
    source_tables: DashboardSummarySource[];
    data_source: DashboardSummaryDataSource;
    status_message?: string | null;
}

export interface DashboardSummaryState {
    loading: boolean;
    data: DashboardSummary | null;
    error: string;
}

@Injectable({ providedIn: 'root' })
export class DashboardSummaryService {
    private readonly apiUrl = '/api/dashboard/summary';
    private readonly fallbackUrl = '/assets/data/dashboard-summary-2023.json';
    private readonly stateSubject = new BehaviorSubject<DashboardSummaryState>({
        loading: false,
        data: null,
        error: '',
    });
    private loadPromise: Promise<void> | null = null;

    public readonly state$ = this.stateSubject.asObservable();

    constructor(@Inject( HttpClient)         private http: HttpClient,@Inject( RegionSelectionService)         private regionSelection: RegionSelectionService,    ) { }

    public get state(): DashboardSummaryState {
        return this.stateSubject.value;
    }

    public load(force = false): Promise<void> {
        if (this.loadPromise) return this.loadPromise;
        if (!force && this.stateSubject.value.data) return Promise.resolve();

        this.loadPromise = this.fetchSummary();
        return this.loadPromise.finally(() => {
            this.loadPromise = null;
        });
    }

    public retry(): Promise<void> {
        return this.load(true);
    }

    private async fetchSummary(): Promise<void> {
        const current = this.stateSubject.value;
        this.stateSubject.next({ loading: true, data: current.data, error: '' });

        try {
            await this.regionSelection.loadTree();
            const referenceYear = this.regionSelection.referenceYear;
            const apiUrl = referenceYear
                ? `${this.apiUrl}?reference_year=${encodeURIComponent(referenceYear)}`
                : this.apiUrl;
            const apiPayload = await firstValueFrom(
                this.http.get(apiUrl, { headers: { Accept: 'application/json' } }).pipe(
                    timeout({ first: 5000 })
                )
            );
            const data = this.normalize(apiPayload, 'API');
            if (referenceYear && data.reference_year !== referenceYear) {
                throw new Error('Dashboard summary reference year mismatch');
            }
            this.stateSubject.next({ loading: false, data, error: '' });
            return;
        } catch (apiError) {
            try {
                const referenceYear = this.regionSelection.referenceYear;
                if (referenceYear && referenceYear !== 2023) {
                    throw new Error('No validated fallback exists for the selected reference year');
                }
                const fallbackPayload = await firstValueFrom(
                    this.http.get(this.fallbackUrl, { headers: { Accept: 'application/json' } }).pipe(
                        timeout({ first: 5000 })
                    )
                );
                const data = this.normalize(fallbackPayload, 'FALLBACK');
                if (referenceYear && data.reference_year !== referenceYear) {
                    throw new Error('Fallback reference year mismatch');
                }
                this.stateSubject.next({ loading: false, data, error: '' });
                return;
            } catch (fallbackError) {
                this.stateSubject.next({
                    loading: false,
                    data: current.data,
                    error: '요약 데이터를 불러오지 못했습니다. 다시 시도해 주세요.',
                });
            }
        }
    }

    private normalize(payload: any, source: DashboardSummaryDataSource): DashboardSummary {
        const raw = payload?.data?.total_registered_regions !== undefined ? payload.data : payload;
        if (!raw || typeof raw !== 'object') throw new Error('Invalid dashboard summary payload');

        const statusValues = [
            raw.ready_regions,
            raw.pending_regions,
            raw.unavailable_regions,
            raw.total_registered_regions,
        ];
        if (statusValues.some(value => !Number.isInteger(value) || value < 0)) {
            throw new Error('Invalid dashboard summary status counts');
        }
        if (raw.ready_regions + raw.pending_regions + raw.unavailable_regions !== raw.total_registered_regions) {
            throw new Error('Inconsistent dashboard summary status counts');
        }
        if (!Number.isInteger(raw.analyzed_industry_count) || raw.analyzed_industry_count < 0) {
            throw new Error('Invalid analyzed industry count');
        }

        return {
            reference_year: raw.reference_year ?? null,
            classification_name: raw.classification_name || '분류 기준 확인 필요',
            industry_scope_label: raw.industry_scope_label || '산업 범위 확인 필요',
            total_registered_regions: raw.total_registered_regions,
            ready_regions: raw.ready_regions,
            pending_regions: raw.pending_regions,
            unavailable_regions: raw.unavailable_regions,
            analyzed_industry_count: raw.analyzed_industry_count,
            national_establishments: raw.national_establishments ?? null,
            national_employees: raw.national_employees ?? null,
            lq_verified_region_industry_pairs: Number.isInteger(raw.lq_verified_region_industry_pairs)
                ? raw.lq_verified_region_industry_pairs
                : 0,
            updated_at: raw.updated_at || null,
            source_tables: Array.isArray(raw.source_tables) ? raw.source_tables : [],
            data_source: source,
            status_message: raw.status_message || null,
        };
    }
}
